import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  TrendingUp, 
  Target, 
  Calendar, 
  BookOpen,
  FileText,
  MessageSquare,
  Briefcase,
  ChevronRight,
  Brain,
  Zap,
  ArrowRight, 
  MapPin
} from 'lucide-react';
import { SkillAssessmentForm } from '../components/career/SkillAssessmentForm';
import { useProfile } from '../hooks/auth/useProfile';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ResumeStrengthCard } from '../components/resume/ResumeStrengthCard';
import { CareerGoalTracker } from '../components/career/CareerGoalTracker';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useUser } from '../context/UserContext';
import { formatDate } from '../utils/dateUtils';

// Blog post interface
interface BlogPost {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  thumbnail: string;
  enclosure?: {
    link: string;
    type: string;
  };
}

export function PrestigeDashboard() {
  const { profile, isLoading } = useProfile();
  const { user } = useAuth();
  const { skills, deleteSkill } = useUser();
  const navigate = useNavigate();
  const [showSkillForm, setShowSkillForm] = useState(false);
  const [registeredEvents, setRegisteredEvents] = useState<any[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoadingBlog, setIsLoadingBlog] = useState(true);
  
  // RSS2JSON API URL
  const RSS_URL = 'https://api.rss2json.com/v1/api.json?rss_url=https://calculatedmoves.substack.com/feed';
  const SUBSTACK_URL = 'https://calculatedmoves.substack.com';

  useEffect(() => {
    if (user) {
      loadRegisteredEvents();
      fetchBlogPosts();
    }
  }, [user]);

  const fetchBlogPosts = async () => {
    setIsLoadingBlog(true);
    try {
      const response = await fetch(RSS_URL);
      
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }
      
      const data = await response.json();
      
      // Check if we have valid items
      if (data?.items?.length) {
        // Log full response to debug
        console.log('Blog feed data:', data.items);
        setBlogPosts(data.items.slice(0, 3)); // Get the latest 3 posts
      } else {
        console.error('No posts found in feed');
        setBlogPosts([]);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setBlogPosts([]);
    } finally {
      setIsLoadingBlog(false);
    }
  };

  // Helper function to get the best image for a post
  const getPostImage = (post: BlogPost) => {
    // First try the thumbnail from RSS2JSON
    if (post.thumbnail && post.thumbnail !== '' && !post.thumbnail.includes('substack-post-image')) {
      return post.thumbnail;
    }
    
    // Then try enclosure link if available
    if (post.enclosure && post.enclosure.link && post.enclosure.type.startsWith('image/')) {
      return post.enclosure.link;
    }
    
    // Extract the first image from the post content if available
    const imgRegex = /<img[^>]+src="([^">]+)"/;
    const contentImage = post.description ? imgRegex.exec(post.description) : null;
    if (contentImage && contentImage[1]) {
      return contentImage[1];
    }
    
    // Fallback to a default image
    return 'https://images.unsplash.com/photo-1497366811353-6870744d04b2';
  };

  const loadRegisteredEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('event_registrations')
        .select(`
          id,
          status,
          created_at,
          events (
            id,
            title,
            date,
            time,
            location,
            type,
            image_url
          )
        `)
        .eq('user_id', user?.id)
        .eq('status', 'confirmed')
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      
      // Filter events that are in the future
      const today = new Date().toISOString().split('T')[0];
      const filteredEvents = data?.filter(reg => 
        reg.events && reg.events.date >= today
      ) || [];
      
      // Sort by event date
      filteredEvents.sort((a, b) => 
        new Date(a.events.date).getTime() - new Date(b.events.date).getTime()
      );
      
      setRegisteredEvents(filteredEvents);
    } catch (error) {
      console.error('Error loading registered events:', error);
      toast.error('Failed to load registered events');
    } finally {
      setIsLoadingEvents(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2a1114] py-8" style={{ fontFamily: 'Open Sans' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <div className="bg-[#2a1114] rounded-xl p-6 pl-0 mb-8">
          <div className="flex items-start justify-between">
            <div className="pl-0">
              <h1 className="text-3xl text-white mb-2 text-left" style={{ fontFamily: 'Open Sans' }}>
                Welcome back, <i>{profile?.first_name}</i>
              </h1>
              <p className="text-gray-300 text-left" style={{ fontFamily: 'Open Sans' }}>
                Your Career Lab Dashboard
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-300" style={{ fontFamily: 'Open Sans' }}>Member since</div>
              <div className="text-white font-medium" style={{ fontFamily: 'Open Sans' }}>
                {new Date(profile?.created_at || '').toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Latest Blog Posts (Replaces Career Progress) */}
          <div className="bg-[#2a1114] rounded-xl p-4 lg:p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <BookOpen className="h-6 w-6 text-black" />
              </div>
              <a 
                href={SUBSTACK_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#ffffff] hover:opacity-80 text-sm flex items-center"
              >
                All Posts
                <ChevronRight className="h-4 w-4 ml-1" />
              </a>
            </div>
            <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Open Sans' }}>Latest Blog Posts</h3>
            
            {isLoadingBlog ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : blogPosts.length > 0 ? (
              <div className="space-y-3">
                {blogPosts.map((post, index) => (
                  <div key={index} className="flex items-start space-x-3 pb-3 border-b border-white last:border-0">
                    <img 
                      src={getPostImage(post)} 
                      alt="" 
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        // Fallback if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://images.unsplash.com/photo-1497366811353-6870744d04b2';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white mb-1">
                        {formatDate(post.pubDate)}
                      </div>
                      <h4 className="font-medium text-white text-sm line-clamp-1">
                        {post.title}
                      </h4>
                      <a 
                        href={post.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-[#ffffff] hover:underline mt-1 inline-block"
                      >
                        Read more
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600">No blog posts found</p>
                <a 
                  href={SUBSTACK_URL}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#ffffff] hover:underline text-sm"
                >
                  Visit Substack
                </a>
              </div>
            )}
          </div>

          {user && <ResumeStrengthCard userId={user.id} />}

          <div className="bg-[#9d9a98] rounded-xl p-4 lg:p-6 shadow-lg h-full overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Calendar className="h-6 w-6 text-[#800020] mr-2" />
                <h3 className="text-lg font-semibold text-black" style={{ fontFamily: 'Open Sans' }}>Upcoming Events</h3>
              </div>
              <Link to="/events" className="text-[#800020] hover:opacity-80 text-sm flex items-center">
                View All
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            {isLoadingEvents ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : registeredEvents.length > 0 ? (
              <div className="space-y-4">
                {registeredEvents.slice(0, 3).map((registration) => (
                  <div key={registration.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="relative h-24 mb-3 rounded-lg overflow-hidden">
                      <img
                        src={registration.events.image_url || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=2070&q=80'}
                        alt={registration.events.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/20"></div>
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          registration.events.type === 'networking'
                            ? 'bg-blue-100 text-blue-800'
                            : registration.events.type === 'workshop'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {registration.events.type}
                        </span>
                      </div>
                    </div>
                    <h4 className="font-medium text-black">{registration.events.title}</h4>
                    <div className="mt-2 flex items-center text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span className="text-sm">
                        {new Date(registration.events.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500 mb-4">No upcoming events</p>
                <Link
                  to="/events"
                  className="text-[#800020] hover:opacity-80 inline-flex items-center"
                >
                  Browse available events
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            )}
          </div>

          <div className="bg-[#9d9a98] rounded-xl p-4 lg:p-6 shadow-lg h-full overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Brain className="h-6 w-6 text-[#800020]" />
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowSkillForm(true)}
                  className="px-4 py-2 bg-[#2a1114] text-white rounded-lg hover:opacity-90 transition-colors text-sm"
                >
                  Manage Skills
                </button>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-black mb-1" style={{ fontFamily: 'Open Sans' }}>Skill Mastery</h3>
            <div className="mt-4 space-y-4">
              {skills.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {skills.map((skill) => (
                    <div key={skill.name} className="bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex-1">
                          <span className="text-sm text-black font-medium">{skill.name}</span>
                          <span className="text-xs text-gray-600 capitalize ml-2">({skill.level})</span>
                        </div>
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove ${skill.name} skill?`)) {
                              deleteSkill(skill.name);
                            }
                          }}
                          className="text-xs text-red-500 hover:text-red-600 transition-colors px-1.5 py-0.5 rounded hover:bg-red-50"
                        >
                          ×
                        </button>
                      </div>
                      <div className="mt-1.5 bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-black h-1.5 rounded-full transition-all"
                          style={{ 
                            width: `${
                              skill.level === 'beginner' ? '33%' :
                              skill.level === 'intermediate' ? '66%' :
                              '100%'
                            }`
                          }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">
                        Updated: {new Date(skill.lastUpdated).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-2">No skills added yet</p>
                  <button
                    onClick={() => setShowSkillForm(true)}
                    className="text-[#800020] hover:opacity-80 text-sm"
                  >
                    Add your first skill
                  </button>
                </div>
              )}
            </div>
            {showSkillForm && (
              <SkillAssessmentForm
                onClose={() => setShowSkillForm(false)}
                onSuccess={() => {
                  setShowSkillForm(false);
                  window.location.reload();
                }}
              />
            )}
          </div>
        </div>

        {/* Career Goal Tracker */}
        <div className="mb-8">
          <CareerGoalTracker />
        </div>

        {/* AI Recommendations */}
        <div className="bg-[#9d9a98] rounded-xl p-4 lg:p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Zap className="h-6 w-6 text-[#800020] mr-2" />
              <h2 className="text-xl font-semibold text-black" style={{ fontFamily: 'Open Sans' }}>AI Career Recommendations</h2>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-4 lg:p-6">
              <h3 className="font-semibold text-black mb-4">Next Career Steps</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-green-600 text-sm">1</span>
                  </div>
                  <span className="text-gray-700">Complete advanced negotiation workshop</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-green-600 text-sm">2</span>
                  </div>
                  <span className="text-gray-700">Update LinkedIn with recent achievements</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-green-600 text-sm">3</span>
                  </div>
                  <span className="text-gray-700">Apply for senior positions in tech startups</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 lg:p-6">
              <h3 className="font-semibold text-black mb-4">Skill Development</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 text-sm">1</span>
                  </div>
                  <span className="text-gray-700">Master system design principles</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 text-sm">2</span>
                  </div>
                  <span className="text-gray-700">Enhance leadership capabilities</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 text-sm">3</span>
                  </div>
                  <span className="text-gray-700">Learn cloud architecture patterns</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 lg:p-6">
              <h3 className="font-semibold text-black mb-4">Network Building</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-purple-600 text-sm">1</span>
                  </div>
                  <span className="text-gray-700">Join tech leadership community</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-purple-600 text-sm">2</span>
                  </div>
                  <span className="text-gray-700">Attend upcoming networking event</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-purple-600 text-sm">3</span>
                  </div>
                  <span className="text-gray-700">Connect with industry mentors</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Link to="/resume-review" className="bg-[#9d9a98] rounded-xl p-4 lg:p-6 shadow-lg hover:shadow-xl transition-shadow group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-[#2a1114] rounded-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-1">Resume Lab</h3>
            <p className="text-gray-600 text-sm">Get expert feedback on your resume</p>
          </Link>

          <Link to="/career-chat" className="bg-[#9d9a98] rounded-xl p-4 lg:p-6 shadow-lg hover:shadow-xl transition-shadow group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-[#2a1114] rounded-lg">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-1">Career Coach</h3>
            <p className="text-gray-600 text-sm">Chat with your AI career advisor</p>
          </Link>

          <Link to="/jobs" className="bg-[#9d9a98] rounded-xl p-4 lg:p-6 shadow-lg hover:shadow-xl transition-shadow group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-[#2a1114] rounded-lg">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-1">Job Board</h3>
            <p className="text-gray-600 text-sm">Browse exclusive opportunities</p>
          </Link>

          <Link to="/courses/marketplace" className="bg-[#9d9a98] rounded-xl p-4 lg:p-6 shadow-lg hover:shadow-xl transition-shadow group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-[#2a1114] rounded-lg">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-1">Course Market</h3>
            <p className="text-gray-600 text-sm">Enhance your skills</p>
          </Link>
        </div>
      </div>
    </div>
  );
}